<script setup>
import Keywords from "@/components/Keywords.vue";
import VClamp from "vue-clamp";
import Vue, {computed, inject, onMounted, ref} from "vue";
import {getLogo} from "@/helpers/logo";
import trackersService from "@/services/trackers.service";
import {trackerTypes} from "@/constants/trackers";
import {useStore} from "vue2-helpers/vuex";

const emit = defineEmits(["loaded"]);
const store = useStore();

const trackerId = inject("tracker").value.id;
const trackerType = inject("tracker").value.type;
const companiesCount = inject("companiesCount");
const companiesIds = inject("companiesIds");
const addCompany = inject("addCompany");
const removeCompany = inject("removeCompany");
const updatingCompanies = inject("updatingCompanies");

const showModal = ref(false);
const suggestedKeywords = ref(null);
const suggestedStartups = ref([]);
const modalOpenedManually = ref(false);

const searchWithKeywordsLink = computed(() =>
    suggestedKeywords.value?.length
        ? `/startups?keywords=${suggestedKeywords.value.join('&keywords=')}`
        : '/startups'
);

onMounted(() => {
    if (trackerType === trackerTypes.PRIVATE && store.getters.isPremiumEnterprise && !companiesCount.value) {
        getSuggestedStartups();
    }
});

async function getSuggestedKeywords() {
    const {data} = await trackersService.getSuggestedKeywords(trackerId);
    suggestedKeywords.value = data;
}

async function getSuggestedStartups() {
    if (suggestedKeywords.value === null) {
        await getSuggestedKeywords();
    }

    if (suggestedKeywords.value.length) {
        const {data} = await trackersService.getSuggestedStartups(trackerId, suggestedKeywords.value);

        if (data.length) {
            suggestedStartups.value = data;
            showModal.value = true;
        } else if (modalOpenedManually.value) {
            Vue.notify({
                group: 'app',
                type: 'error',
                duration: 5000,
                text: "No suggested startups found"
            })
        }
    } else if (modalOpenedManually.value) {
        Vue.notify({
            group: 'app',
            type: 'error',
            duration: 5000,
            text: "No suggested keywords found"
        })
    }

    emit("loaded");
}

function open() {
    modalOpenedManually.value = true;
    suggestedStartups.value = [];
    getSuggestedStartups();
}

defineExpose({
    open,
});
</script>

<template>
    <b-modal v-model="showModal" title="Potential Matches" hide-footer modal-class="modal-suggested-startups modal-w-866">
        <div class="keywords-block mb-3">
            <p>Suggested keywords:</p>

            <keywords
                v-model="suggestedKeywords"
            />
        </div>

        <ul class="list-startups">
            <li v-for="(startup, index) in suggestedStartups" :key="index">
                <div class="image-block-wrapper">
                    <div class="image-block image-block--39">
                        <img :src="getLogo(startup.logo)" alt="">
                    </div>
                    <h5 class="heading5">
                        <a :href="'/startups/' + startup.id" target="_blank">{{ startup.name }}</a>
                    </h5>
                </div>

                <div class="list-startups__description">

                    <v-clamp autoresize :max-lines="3">
                        {{ startup.description }}
                        <template #after="{ toggle, expanded, clamped }">
                            <a v-if="expanded" class="link" @click="toggle">less</a>
                            <a v-if="clamped" class="link" @click="toggle">more</a>
                        </template>
                    </v-clamp>
                </div>

                <footer class="list-startups__footer">
                    <button v-if="companiesIds.includes(startup.id)" :disabled="updatingCompanies" class="btn btn-red btn-sm"
                            @click="removeCompany(startup.id)">- Remove from tracker
                    </button>

                    <button v-else :disabled="updatingCompanies" class="button button--sm" @click="addCompany(startup.id)">+
                        Add to tracker
                    </button>
                </footer>
            </li>
        </ul>

        <footer class="modal-suggested-startups__footer">
            <a :href="searchWithKeywordsLink" target="_blank" class="btn btn-bordered">Find More Startups</a>
        </footer>
    </b-modal>
</template>

<style scoped>

</style>
